import styled from '@emotion/styled';
import { ob } from '../../onboardingV2.tokens';

export const FieldWrap = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

export const Label = styled.label`
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 1.4;
    font-weight: 600;
    color: ${ob.color.label};

    small {
        font-size: 14px;
        font-weight: 400;
        color: ${ob.color.muted};
    }
`;

export const InputShell = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    min-height: ${ob.control};
    border: 1.5px solid ${({ invalid }) => (invalid ? ob.color.error : ob.color.border)};
    border-radius: ${ob.radius.md};
    background: ${ob.color.surface};
    transition:
        border-color ${ob.motion.fast},
        box-shadow ${ob.motion.fast};

    ${ob.hover} {
        &:hover {
            border-color: ${({ invalid }) => (invalid ? ob.color.error : ob.color.borderHover)};
        }
    }

    &:focus-within {
        border-color: ${({ invalid }) => (invalid ? ob.color.error : ob.color.brand)};
        box-shadow: ${({ invalid }) => (invalid ? ob.focusInputError : ob.focusInput)};
    }

    fieldset:disabled & {
        background: ${ob.color.inputDisabledBg};
    }
`;

// Forwards every prop: Emotion's default filter drops `enterkeyhint` (unknown to React 16).
// Only DOM attributes are ever passed to this element.
export const Input = styled('input', { shouldForwardProp: (prop) => prop !== 'theme' })`
    flex: 1;
    width: 100%;
    min-width: 0;
    height: 49px;
    padding: 0 16px;
    border: 0;
    outline: none;
    background: transparent;
    font-family: ${ob.font.body};
    font-size: 16px;
    line-height: 1.25;
    color: ${ob.color.text};
    border-radius: ${ob.radius.md};
    -webkit-appearance: none;
    appearance: none;

    &::placeholder {
        color: ${ob.color.muted};
        opacity: 1;
    }

    &:disabled {
        color: ${ob.color.muted};
        -webkit-text-fill-color: ${ob.color.muted};
        opacity: 1;
    }

    &[inputmode='numeric'],
    &[type='tel'] {
        font-variant-numeric: tabular-nums;
    }
`;

export const Affix = styled.span`
    flex-shrink: 0;
    padding: ${({ side }) => (side === 'left' ? '0 0 0 16px' : '0 16px 0 0')};
    font-size: ${({ side }) => (side === 'left' ? '16px' : '13px')};
    font-weight: ${({ side }) => (side === 'left' ? 500 : 600)};
    color: ${ob.color.muted};

    & + input {
        padding-left: 8px;
    }
`;

export const IconAction = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${ob.tap};
    height: ${ob.tap};
    margin-right: 4px;
    padding: 0;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: ${({ subtle }) => (subtle ? ob.color.borderHover : ob.color.body)};
    cursor: pointer;

    i {
        font-size: 20px;
        line-height: 1;
    }

    &:focus-visible {
        outline: none;
        box-shadow: ${ob.focusRing};
    }
`;

export const HelperRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

export const Hint = styled.p`
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 1.4;
    color: ${({ ok }) => (ok ? ob.color.success : ob.color.muted)};

    i {
        font-size: 16px;
        color: ${ob.color.lime700};
    }
`;

export const Counter = styled.span`
    margin-top: 6px;
    font-size: 12px;
    color: ${ob.color.muted};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const ErrorText = styled.div`
    &:empty {
        display: none;
    }

    p {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        margin: 6px 0 0;
        font-size: 13px;
        line-height: 1.4;
        font-weight: 500;
        color: ${ob.color.error};
    }

    i {
        font-size: 15px;
        line-height: 1.25;
    }

    a,
    button {
        color: ${ob.color.error};
        font-weight: 700;
        text-decoration: underline;
    }
`;

export const SelectSegment = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 6px;
    align-self: stretch;
    min-width: 104px;
    padding: 0 10px 0 16px;
    font-size: 16px;
    font-weight: 500;
    color: ${ob.color.text};
    font-variant-numeric: tabular-nums;

    &::after {
        content: '';
        position: absolute;
        right: 0;
        top: 50%;
        width: 1px;
        height: 24px;
        transform: translateY(-50%);
        background: ${ob.color.borderSubtle};
    }

    i {
        font-size: 18px;
        color: ${ob.color.muted};
    }

    select {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        font-size: 16px;
        cursor: pointer;
        border: 0;
    }

    &:focus-within {
        border-radius: ${ob.radius.md} 0 0 ${ob.radius.md};
        box-shadow: inset 0 0 0 2px ${ob.color.brand};
    }
`;

/** Right-side native `<select>` (transparent over "COP ▾"), e.g. the price currency. */
export const EndSelect = styled(SelectSegment)`
    min-width: 0;
    padding: 0 14px 0 12px;
    font-size: 15px;
    color: ${ob.color.muted};

    &::after {
        left: 0;
        right: auto;
    }

    &:focus-within {
        border-radius: 0 ${ob.radius.md} ${ob.radius.md} 0;
    }
`;

export const Flag = styled.span`
    font-size: 20px;
    line-height: 1;
`;
